import { useCallback, useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { escapeIlikePattern, normalizeProductSearchToken } from "@/lib/product-search";
import type { WhatsAppProviderId } from "@/lib/whatsapp-provider-capabilities";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import {
  ArrowLeft,
  Check,
  Copy,
  CreditCard,
  ExternalLink,
  ImagePlus,
  Loader2,
  Package,
  Send,
  Settings,
} from "lucide-react";
import { ProductSendPreviewDialog } from "./ProductSendPreviewDialog";
import { useWorkspacePlan } from "@/hooks/useWorkspacePlan";

type ProductType = "product" | "service" | "plan";
type TypeFilter = "all" | ProductType;

const TYPE_FILTER_OPTIONS: { value: TypeFilter; label: string }[] = [
  { value: "all", label: "Todos" },
  { value: "product", label: "Produtos" },
  { value: "service", label: "Serviços" },
  { value: "plan", label: "Planos" },
];

interface ProductListItem {
  id: string;
  name: string;
  type: ProductType;
  price?: number | null;
  sku?: string | null;
  description?: string | null;
  stock?: number | null;
  image_url?: string | null;
  is_active?: boolean | null;
}

interface ProductDetail extends ProductListItem {
  dimensions?: string | null;
  colors?: string[] | null;
  measurements?: string[] | null;
  duration?: number | null;
  service_category?: string | null;
  product_url?: string | null;
  plan_billing_type?: string | null;
  plan_trial_days?: number | null;
  plan_features?: string[] | null;
  wholesale_enabled?: boolean | null;
  wholesale_price?: number | null;
  wholesale_min_quantity?: number | null;
}

interface PlanPriceRow {
  price: number;
  period: string;
  period_label: string | null;
}

const TYPE_LABEL: Record<ProductType, string> = {
  product: "Produto",
  service: "Serviço",
  plan: "Plano",
};

const TYPE_ICON: Record<ProductType, typeof Package> = {
  product: Package,
  service: Settings,
  plan: CreditCard,
};

const PERIOD_SUFFIX: Record<string, string> = {
  monthly: "/mês",
  quarterly: "/trim",
  semiannual: "/sem",
  annual: "/ano",
  one_time: "",
  custom: "",
};

function formatMoney(value: number): string {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);
}

function formatListPrice(product: ProductListItem): string {
  if (product.type === "plan" || product.price == null) return "";
  return formatMoney(product.price);
}

function buildProductSummaryMessage(
  product: ProductDetail,
  planPrices: PlanPriceRow[],
): string {
  const lines: string[] = [];

  lines.push(`*${product.name}*`);

  if (product.sku) {
    lines.push(`_SKU: ${product.sku}_`);
  }

  const priceLines: string[] = [];
  if (product.type === "plan") {
    if (planPrices.length > 0) {
      for (const pp of planPrices) {
        const label = pp.period_label || pp.period;
        priceLines.push(
          `• ${label}: ${formatMoney(pp.price)}${PERIOD_SUFFIX[pp.period] ?? ""}`,
        );
      }
    } else if (product.price != null) {
      priceLines.push(formatMoney(product.price));
    }
  } else if (product.price != null) {
    priceLines.push(formatMoney(product.price));
  }

  if (priceLines.length > 0) {
    lines.push("");
    lines.push("*Preço*");
    lines.push(...priceLines);
  }

  if (product.wholesale_enabled && product.wholesale_price != null) {
    const wholesale = `Atacado: ${formatMoney(product.wholesale_price)}${
      product.wholesale_min_quantity
        ? ` (mín. ${product.wholesale_min_quantity} un.)`
        : ""
    }`;
    lines.push(wholesale);
  }

  if (product.description?.trim()) {
    lines.push("");
    lines.push("*Descrição*");
    lines.push(product.description.trim());
  }

  const details: string[] = [];
  if (product.type === "product" && product.stock != null) {
    details.push(`Estoque: ${product.stock}`);
  }
  if (product.dimensions) details.push(`Dimensões: ${product.dimensions}`);
  if (product.type === "service" && product.duration != null) {
    details.push(`Duração: ${product.duration} min`);
  }
  if (product.plan_trial_days != null && product.plan_trial_days > 0) {
    details.push(`Trial: ${product.plan_trial_days} dias`);
  }

  if (details.length > 0) {
    lines.push("");
    lines.push("*Detalhes*");
    for (const item of details) lines.push(`• ${item}`);
  }

  if (product.colors?.length) {
    lines.push("");
    lines.push("*Cores*");
    lines.push(product.colors.join(", "));
  }

  if (product.measurements?.length) {
    lines.push("");
    lines.push("*Medidas*");
    for (const measurement of product.measurements) {
      lines.push(`• ${measurement}`);
    }
  }

  if (product.plan_features?.length) {
    lines.push("");
    lines.push("*Benefícios do plano*");
    for (const feature of product.plan_features) {
      lines.push(`• ${feature}`);
    }
  }

  if (product.product_url) {
    lines.push("");
    lines.push(product.product_url);
  }

  return lines.join("\n").trim();
}

interface ChatProductLookupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  onInsertToChat?: (text: string) => void;
  conversationId?: string | null;
  channel?: string;
  whatsappProvider?: WhatsAppProviderId;
}

export function ChatProductLookupDialog({
  open,
  onOpenChange,
  workspaceId,
  onInsertToChat,
  conversationId,
  channel = "whatsapp",
  whatsappProvider,
}: ChatProductLookupDialogProps) {
  const { toast } = useToast();
  const { productImagesAllowed } = useWorkspacePlan(workspaceId);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const [results, setResults] = useState<ProductListItem[]>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<ProductDetail | null>(null);
  const [planPrices, setPlanPrices] = useState<PlanPriceRow[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [copiedSummary, setCopiedSummary] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const reset = useCallback(() => {
    setSearchQuery("");
    setTypeFilter("all");
    setResults([]);
    setSelected(null);
    setPlanPrices([]);
    setDetailLoading(false);
    setCopiedSummary(false);
    if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
  }, []);

  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  const searchProducts = useCallback(
    async (term: string, filter: TypeFilter) => {
      if (!workspaceId) return;
      setSearching(true);
      try {
        let q = supabase
          .from("products")
          .select("id, name, type, price, sku, description, stock, image_url, is_active")
          .eq("workspace_id", workspaceId)
          .limit(40);

        if (filter !== "all") {
          q = q.eq("type", filter);
        }

        const trimmed = term.trim();
        if (trimmed.length > 0) {
          const escaped = escapeIlikePattern(trimmed);
          const normalized = normalizeProductSearchToken(trimmed);
          q = q.or(
            [
              `name.ilike.%${escaped}%`,
              `sku.ilike.%${escaped}%`,
              `description.ilike.%${escaped}%`,
              normalized ? `name_normalized.ilike.%${normalized}%` : null,
              normalized ? `sku_normalized.ilike.%${normalized}%` : null,
            ]
              .filter(Boolean)
              .join(","),
          );
          q = q.order("name", { ascending: true });
        } else {
          q = q.order("name", { ascending: true });
        }

        const { data, error } = await q;
        if (error) throw error;
        setResults((data || []) as ProductListItem[]);
      } catch (error) {
        console.error("Error searching products:", error);
        setResults([]);
      } finally {
        setSearching(false);
      }
    },
    [workspaceId],
  );

  useEffect(() => {
    if (!open || !workspaceId) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(
      () => searchProducts(searchQuery, typeFilter),
      searchQuery.trim().length === 0 ? 0 : 300,
    );
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [open, workspaceId, searchQuery, typeFilter, searchProducts]);

  const getSummaryText = () => {
    if (!selected) return "";
    return buildProductSummaryMessage(selected, planPrices);
  };

  const handleCopySummary = async () => {
    if (!selected) return;

    const summary = getSummaryText();
    try {
      await navigator.clipboard.writeText(summary);
      setCopiedSummary(true);
      if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
      copiedTimeoutRef.current = setTimeout(() => setCopiedSummary(false), 2000);
      toast({
        title: "Resumo copiado",
        description: "Cole no chat para enviar ao cliente.",
      });
    } catch {
      toast({
        title: "Não foi possível copiar",
        description: "Permita acesso à área de transferência.",
        variant: "destructive",
      });
    }
  };

  const handleInsertToChat = () => {
    if (!selected || !onInsertToChat) return;

    const summary = getSummaryText();
    if (!summary) return;

    onInsertToChat(summary);
    onOpenChange(false);
    toast({
      title: "Resumo no chat",
      description: "Revise a mensagem e envie quando estiver pronto.",
    });
  };

  const openDetail = async (productId: string) => {
    setDetailLoading(true);
    setPlanPrices([]);
    setCopiedSummary(false);
    try {
      const { data, error } = await supabase
        .from("products")
        .select(
          "id, name, type, price, sku, description, stock, image_url, is_active, dimensions, colors, measurements, duration, service_category, product_url, plan_billing_type, plan_trial_days, plan_features, wholesale_enabled, wholesale_price, wholesale_min_quantity",
        )
        .eq("id", productId)
        .maybeSingle();
      if (error) throw error;
      if (!data) return;
      setSelected(data as ProductDetail);

      if (data.type === "plan") {
        const { data: prices } = await supabase
          .from("product_plan_prices")
          .select("price, period, period_label")
          .eq("product_id", productId)
          .order("price", { ascending: true });
        setPlanPrices((prices || []) as PlanPriceRow[]);
      }
    } catch (error) {
      console.error("Error loading product detail:", error);
    } finally {
      setDetailLoading(false);
    }
  };

  const getProductPriceLabel = (): string | null => {
    if (!selected) return null;
    if (selected.type === "plan") {
      if (planPrices.length > 0) {
        const pp = planPrices[0];
        return `${formatMoney(pp.price)}${PERIOD_SUFFIX[pp.period] ?? ""}`;
      }
      return selected.price != null ? formatMoney(selected.price) : null;
    }
    return selected.price != null ? formatMoney(selected.price) : null;
  };

  const renderDetail = () => {
    if (detailLoading || !selected) {
      return (
        <div className="flex min-h-[200px] items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      );
    }

    const TypeIcon = TYPE_ICON[selected.type] ?? Package;

    return (
      <ScrollArea className="max-h-[min(70vh,560px)] pr-3">
        <div className="space-y-4">
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => void handleCopySummary()}
            >
              {copiedSummary ? (
                <>
                  <Check className="h-4 w-4" />
                  Copiado
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4" />
                  Copiar
                </>
              )}
            </Button>
            {onInsertToChat && (
              <Button
                type="button"
                variant="default"
                className="flex-1"
                onClick={handleInsertToChat}
              >
                <Send className="h-4 w-4" />
                Inserir texto
              </Button>
            )}
            {productImagesAllowed && conversationId && channel === "whatsapp" && (
              <Button
                type="button"
                variant="default"
                className="flex-1"
                onClick={() => setPreviewOpen(true)}
                title="Enviar texto + imagens diretamente ao cliente"
              >
                <ImagePlus className="h-4 w-4" />
                Enviar com imagens
              </Button>
            )}
          </div>

          <div className="flex gap-3">
            {selected.image_url ? (
              <img
                src={selected.image_url}
                alt={selected.name}
                className="h-20 w-20 shrink-0 rounded-lg border object-cover"
              />
            ) : (
              <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-lg border bg-muted">
                <TypeIcon className="h-8 w-8 text-muted-foreground" />
              </div>
            )}
            <div className="min-w-0 flex-1 space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="secondary">{TYPE_LABEL[selected.type]}</Badge>
                {selected.is_active === false && (
                  <Badge variant="outline" className="text-muted-foreground">
                    Inativo
                  </Badge>
                )}
              </div>
              <h3 className="text-base font-semibold leading-snug">{selected.name}</h3>
              {selected.sku && (
                <p className="text-xs text-muted-foreground">SKU: {selected.sku}</p>
              )}
            </div>
          </div>

          <div className="rounded-lg border bg-muted/30 p-3 space-y-2 text-sm">
            <p className="font-medium text-foreground">Preço</p>
            {selected.type === "plan" ? (
              planPrices.length > 0 ? (
                <ul className="space-y-1">
                  {planPrices.map((pp, i) => (
                    <li key={i} className="flex justify-between gap-2">
                      <span className="text-muted-foreground">
                        {pp.period_label || pp.period}
                      </span>
                      <span className="font-medium">
                        {formatMoney(pp.price)}
                        {PERIOD_SUFFIX[pp.period] ?? ""}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : selected.price != null ? (
                <p className="font-medium">{formatMoney(selected.price)}</p>
              ) : (
                <p className="text-muted-foreground">Sem preço cadastrado</p>
              )
            ) : selected.price != null ? (
              <p className="text-lg font-semibold">{formatMoney(selected.price)}</p>
            ) : (
              <p className="text-muted-foreground">Sem preço cadastrado</p>
            )}

            {selected.wholesale_enabled && selected.wholesale_price != null && (
              <p className="text-xs text-muted-foreground pt-1 border-t">
                Atacado: {formatMoney(selected.wholesale_price)}
                {selected.wholesale_min_quantity
                  ? ` (mín. ${selected.wholesale_min_quantity} un.)`
                  : ""}
              </p>
            )}
          </div>

          {selected.description && (
            <div className="space-y-1">
              <p className="text-sm font-medium">Descrição</p>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                {selected.description}
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2 text-sm">
            {selected.type === "product" && selected.stock != null && (
              <div className="rounded-md border px-3 py-2">
                <p className="text-xs text-muted-foreground">Estoque</p>
                <p className="font-medium">{selected.stock}</p>
              </div>
            )}
            {selected.dimensions && (
              <div className="rounded-md border px-3 py-2">
                <p className="text-xs text-muted-foreground">Dimensões</p>
                <p className="font-medium">{selected.dimensions}</p>
              </div>
            )}
            {selected.type === "service" && selected.duration != null && (
              <div className="rounded-md border px-3 py-2">
                <p className="text-xs text-muted-foreground">Duração</p>
                <p className="font-medium">{selected.duration} min</p>
              </div>
            )}
            {selected.service_category && (
              <div className="rounded-md border px-3 py-2">
                <p className="text-xs text-muted-foreground">Categoria</p>
                <p className="font-medium">{selected.service_category}</p>
              </div>
            )}
            {selected.plan_trial_days != null && selected.plan_trial_days > 0 && (
              <div className="rounded-md border px-3 py-2">
                <p className="text-xs text-muted-foreground">Trial</p>
                <p className="font-medium">{selected.plan_trial_days} dias</p>
              </div>
            )}
          </div>

          {selected.colors && selected.colors.length > 0 && (
            <div className="space-y-1">
              <p className="text-sm font-medium">Cores</p>
              <p className="text-sm text-muted-foreground">{selected.colors.join(", ")}</p>
            </div>
          )}

          {selected.measurements && selected.measurements.length > 0 && (
            <div className="space-y-1">
              <p className="text-sm font-medium">Medidas</p>
              <ul className="text-sm text-muted-foreground list-disc pl-4">
                {selected.measurements.map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
            </div>
          )}

          {selected.plan_features && selected.plan_features.length > 0 && (
            <div className="space-y-1">
              <p className="text-sm font-medium">Benefícios do plano</p>
              <ul className="text-sm text-muted-foreground list-disc pl-4">
                {selected.plan_features.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
            </div>
          )}

          {selected.product_url && (
            <a
              href={selected.product_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
            >
              Ver página do item
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
        </div>
      </ScrollArea>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-4 pt-4 pb-2 border-b">
          <div className="flex items-center gap-2">
            {selected && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0"
                onClick={() => {
                  setSelected(null);
                  setPlanPrices([]);
                  setCopiedSummary(false);
                }}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            <DialogTitle className="text-base">
              {selected ? selected.name : "Consultar catálogo"}
            </DialogTitle>
          </div>
          {!selected && (
            <p className="text-xs text-muted-foreground font-normal pt-1">
              Busque produtos, serviços ou planos para consultar durante o atendimento.
            </p>
          )}
        </DialogHeader>

        {selected ? (
          <div className="p-4">{renderDetail()}</div>
        ) : (
          <Command shouldFilter={false} className="rounded-none border-0">
            <div className="flex flex-wrap gap-1.5 border-b px-3 py-2.5">
              {TYPE_FILTER_OPTIONS.map((opt) => (
                <Button
                  key={opt.value}
                  type="button"
                  size="sm"
                  variant={typeFilter === opt.value ? "default" : "outline"}
                  className="h-8 px-3 text-xs"
                  onClick={() => setTypeFilter(opt.value)}
                >
                  {opt.label}
                </Button>
              ))}
            </div>
            <CommandInput
              placeholder={
                typeFilter === "all"
                  ? "Buscar por nome, SKU ou descrição..."
                  : `Buscar ${TYPE_FILTER_OPTIONS.find((o) => o.value === typeFilter)?.label.toLowerCase()}...`
              }
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandList className="max-h-[min(60vh,420px)]">
              <CommandEmpty>
                {searching
                  ? "Buscando..."
                  : typeFilter === "all"
                    ? "Nenhum item encontrado."
                    : `Nenhum ${TYPE_LABEL[typeFilter].toLowerCase()} encontrado.`}
              </CommandEmpty>
              <CommandGroup className="p-2">
                {results.map((item) => {
                  const TypeIcon = TYPE_ICON[item.type] ?? Package;
                  const priceLabel = formatListPrice(item);
                  return (
                    <CommandItem
                      key={item.id}
                      value={item.id}
                      onSelect={() => void openDetail(item.id)}
                      className="flex items-start gap-3 py-3 cursor-pointer"
                    >
                      {item.image_url ? (
                        <img
                          src={item.image_url}
                          alt=""
                          className="h-10 w-10 shrink-0 rounded object-cover"
                        />
                      ) : (
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded bg-muted">
                          <TypeIcon className="h-4 w-4 text-muted-foreground" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium truncate">{item.name}</span>
                          {item.is_active === false && (
                            <Badge variant="outline" className="text-[10px] px-1 py-0">
                              Inativo
                            </Badge>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground mt-0.5">
                          <span>{TYPE_LABEL[item.type]}</span>
                          {item.sku && <span>· {item.sku}</span>}
                          {priceLabel && (
                            <span className={cn("text-foreground font-medium")}>
                              · {priceLabel}
                            </span>
                          )}
                        </div>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        )}
      </DialogContent>
      {selected && conversationId && (
        <ProductSendPreviewDialog
          open={previewOpen}
          onOpenChange={setPreviewOpen}
          workspaceId={workspaceId}
          productId={selected.id}
          productName={selected.name}
          conversationId={conversationId}
          channel={channel}
          initialText={getSummaryText()}
          whatsappProvider={whatsappProvider}
          productUrl={selected.product_url}
          productPriceLabel={getProductPriceLabel()}
          onSent={() => onOpenChange(false)}
        />
      )}
    </Dialog>
  );
}
